import { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, Plus, X, Trash2, Check, PieChart } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import AudiencePicker from "../../components/os/AudiencePicker";
import { api } from "../../lib/api";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="poll-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

function PollCard({ p, meta, onVote, onDel, onResults }) {
  const total = p.total_votes || 0;
  const voted = p.my_vote != null;
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`poll-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-base font-medium text-navy">{p.question}</div>
        {meta?.is_leader && <button onClick={() => onDel(p.id)} className="text-navy/30 hover:text-destructive" data-testid={`poll-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
      <div className="text-xs text-navy/45 mt-1">Por {p.created_by_name} · {p.target_count} destinatarios · {total} votos</div>
      <div className="space-y-2 mt-3">
        {p.options.map((o, i) => {
          const c = p.counts?.[i] || 0;
          const pct = total ? Math.round((c / total) * 100) : 0;
          const mine = p.my_vote === i;
          return (
            <button
              key={i}
              onClick={() => onVote(p.id, i)}
              data-testid={`poll-${p.id}-option-${i}`}
              className={`w-full text-left rounded-lg border px-3 py-2 relative overflow-hidden transition-colors ${mine ? "border-violet" : "border-navy/15 hover:border-violet/40"}`}
            >
              {(voted || meta?.is_leader) && <span className="absolute inset-0 bg-violet/10" style={{ width: `${pct}%` }} />}
              <span className="relative flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-navy">{mine && <Check className="w-4 h-4 text-violet" />} {o}</span>
                {(voted || meta?.is_leader) && <span className="text-navy/50 text-xs">{pct}% · {c}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2">
        {voted ? <div className="text-xs text-emerald-600">Tu voto quedó registrado. Puedes cambiarlo.</div> : <span />}
        {meta?.is_leader && <button onClick={() => onResults(p)} data-testid={`poll-results-${p.id}`} className="inline-flex items-center gap-1.5 text-xs text-violet hover:underline"><PieChart className="w-3.5 h-3.5" /> Resultados por área</button>}
      </div>
    </div>
  );
}

function ResultsModal({ data, onClose }) {
  if (!data) return null;
  const opts = data.options || [];
  const palette = ["bg-violet", "bg-emerald-500", "bg-gold", "bg-sky-500", "bg-pink-500", "bg-orange-500"];
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="poll-results-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1"><h3 className="font-editorial text-2xl">Resultados por área</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        <p className="text-sm text-navy/60 mb-1">{data.question}</p>
        <div className="text-xs text-navy/45 mb-4">{data.total_votes} de {data.target_count} respondieron</div>
        <div className="flex flex-wrap gap-3 mb-4">
          {opts.map((o, i) => <span key={i} className="inline-flex items-center gap-1.5 text-xs text-navy/70"><span className={`w-3 h-3 rounded-sm ${palette[i % palette.length]}`} /> {o}</span>)}
        </div>
        {(data.by_area || []).length === 0 ? (
          <div className="text-sm text-navy/40 py-6 text-center">Aún no hay votos.</div>
        ) : (
          <div className="space-y-4">
            {data.by_area.map((row) => (
              <div key={row.area} data-testid={`poll-area-${row.area}`}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-navy">{row.area}</span><span className="text-navy/45 text-xs">{row.total} votos</span></div>
                <div className="flex h-6 rounded-lg overflow-hidden border border-navy/10">
                  {opts.map((o, i) => {
                    const c = row.counts[i] || 0;
                    const pct = row.total ? (c / row.total) * 100 : 0;
                    if (!pct) return null;
                    return <div key={i} className={`${palette[i % palette.length]} flex items-center justify-center text-[10px] text-white`} style={{ width: `${pct}%` }} title={`${o}: ${c}`}>{pct >= 12 ? `${Math.round(pct)}%` : ""}</div>;
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Polls() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const [results, setResults] = useState(null);

  const load = useCallback(() => { api.get("/polls").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/polls/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false, areas: [] })); load(); }, [load]);

  const openResults = async (p) => {
    setResults({ loading: true });
    try { const r = await api.get(`/polls/${p.id}/results`); setResults(r.data); }
    catch (e) { setResults(null); toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openNew = () => { setF({ question: "", options: ["", ""], anonymous: true, aud: { audience: "all", user_ids: [], areas: [] } }); setOpen(true); };
  const setOpt = (i, v) => { const o = [...f.options]; o[i] = v; setF({ ...f, options: o }); };

  const submit = async () => {
    if (!f.question.trim()) return toast.error("Escribe la pregunta");
    const opts = f.options.filter((o) => o.trim());
    if (opts.length < 2) return toast.error("Agrega al menos 2 opciones");
    setBusy(true);
    try {
      await api.post("/polls", {
        question: f.question, options: opts, anonymous: f.anonymous,
        audience: f.aud.audience, user_ids: f.aud.user_ids, areas: f.aud.areas,
      });
      toast.success("Encuesta creada");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const vote = async (id, idx) => { try { const r = await api.post(`/polls/${id}/vote`, { option_index: idx }); setItems((xs) => xs.map((x) => (x.id === id ? r.data : x))); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/polls/${id}`); load(); } catch { toast.error("Error"); } };

  return (
    <OSLayout title="OMNIA Polls">
      <div className="max-w-4xl space-y-6" data-testid="polls-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-navy/55 max-w-xl">Encuestas rápidas de una pregunta. Elige a quién enviarla: toda la empresa, áreas específicas o usuarios seleccionados del directorio.</p>
          {meta?.is_leader && <button onClick={openNew} className="btn btn-primary gap-2" data-testid="poll-new"><Plus className="w-4 h-4" /> Nueva encuesta</button>}
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-navy/10 rounded-2xl p-10 text-center text-navy/40" data-testid="polls-empty">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 text-navy/25" /> No hay encuestas todavía.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {items.map((p) => <PollCard key={p.id} p={p} meta={meta} onVote={vote} onDel={del} onResults={openResults} />)}
          </div>
        )}
      </div>

      {results && !results.loading && <ResultsModal data={results} onClose={() => setResults(null)} />}

      {open && f && (
        <Modal title="Nueva encuesta" onClose={() => setOpen(false)}>
          <div className="space-y-4">
            <input className="field" placeholder="Pregunta" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="poll-question" />
            <div>
              <label className="text-xs font-medium text-navy/70 mb-1 block">Opciones</label>
              {f.options.map((o, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input className="field" placeholder={`Opción ${i + 1}`} value={o} onChange={(e) => setOpt(i, e.target.value)} data-testid={`poll-opt-${i}`} />
                  {f.options.length > 2 && <button onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })} className="text-navy/30 hover:text-destructive"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button type="button" onClick={() => setF({ ...f, options: [...f.options, ""] })} className="text-xs text-violet hover:underline" data-testid="poll-add-opt">+ Añadir opción</button>
            </div>

            <div>
              <label className="text-xs font-medium text-navy/70 mb-2 block">¿A quién enviar?</label>
              <AudiencePicker value={f.aud} onChange={(aud) => setF({ ...f, aud })} areas={meta?.areas || []} testid="poll-aud" />
            </div>

            <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
              <input type="checkbox" checked={f.anonymous} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} data-testid="poll-anon" /> Encuesta anónima
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button>
              <button onClick={submit} disabled={busy} className="btn btn-primary gap-2" data-testid="poll-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear encuesta</button>
            </div>
          </div>
        </Modal>
      )}
    </OSLayout>
  );
}
